import { useMemo, useState } from "react";

import { Banner, Empty } from "../ui";
import { CaseLightbox } from "./lightbox";
import {
  AXES,
  Card,
  MIN_CELL_N,
  avoidableBucket,
  axisLabel,
  fmtInt,
  fmtMs,
  fmtPct,
  num,
  passBucket,
} from "./format";
import type { MatrixCell, SuiteDetail } from "./types";

// ============================================================ matrix tab

interface MatrixTabProps {
  detail: SuiteDetail | null;
  runId: string;
  rowAxis: string;
  rowAxis2: string;
  colAxis: string;
  family: string;
  filters: Record<string, string>;
  onRowAxis: (v: string) => void;
  onRowAxis2: (v: string) => void;
  onColAxis: (v: string) => void;
  onFamily: (v: string) => void;
  onFilter: (key: string, value: string) => void;
  onClearFilters: () => void;
}

interface Margin {
  n: number;
  passed: number;
  pass_rate: number | null;
}

/**
 * GRID AXES ARE THE PRODUCT DECISION. Rows are the COMBINATION of scam stratum
 * and user stratum, columns are language, because the playbook routes off
 * exactly that pair. Both axes are overridable; the default is not a placeholder.
 */
export function MatrixTab({
  detail,
  runId,
  rowAxis,
  rowAxis2,
  colAxis,
  family,
  filters,
  onRowAxis,
  onRowAxis2,
  onColAxis,
  onFamily,
  onFilter,
  onClearFilters,
}: MatrixTabProps) {
  const [colourBy, setColourBy] = useState<"pass" | "avoidable">("pass");
  const [box, setBox] = useState<{
    open: boolean;
    filters: Record<string, string>;
    title: string;
    subtitle: string;
    n: number;
    passed: number;
  }>({ open: false, filters: {}, title: "", subtitle: "", n: 0, passed: 0 });

  const cells = detail?.matrix ?? [];
  const rowKeys = detail?.row_keys ?? [];
  const colKeys = detail?.col_keys ?? [];

  const index = useMemo(() => {
    const out = new Map<string, MatrixCell>();
    for (const c of cells) out.set(`${c.row_key}||${c.col_key}`, c);
    return out;
  }, [cells]);
  const cellAt = (row: string, col: string): MatrixCell | null => index.get(`${row}||${col}`) ?? null;

  // Margins are recomputed FROM the cells rather than requested separately, so a
  // row total can never disagree with the row it sums.
  const margins = useMemo(() => {
    const rows = new Map<string, Margin>();
    const cols = new Map<string, Margin>();
    const add = (m: Map<string, Margin>, key: string, cell: MatrixCell) => {
      const cur = m.get(key) ?? { n: 0, passed: 0, pass_rate: null };
      cur.n += num(cell.n);
      cur.passed += num(cell.passed);
      cur.pass_rate = cur.n ? cur.passed / cur.n : null;
      m.set(key, cur);
    };
    for (const c of cells) {
      add(rows, String(c.row_key), c);
      add(cols, String(c.col_key), c);
    }
    return { rows, cols };
  }, [cells]);

  // Axes this run's harness never filled in. A grid sliced on one of them is a
  // single '(blank)' row, and reading that as "one homogeneous segment" is
  // exactly backwards — so it is named.
  const coverage = detail?.axis_coverage ?? {};
  const nTurns = num(detail?.total);
  const emptyAxes = [rowAxis, rowAxis2, colAxis]
    .filter((a) => a && nTurns > 0 && num(coverage[a]) === 0)
    .map(axisLabel);

  // Filter values come from the run's own turns, not a hardcoded list that goes
  // stale the moment the corpus gains a stratum.
  const axisValues = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    const add = (key: string, value: unknown) => {
      const v = String(value ?? "").trim();
      if (!key || !v) return;
      (out[key] ??= new Set<string>()).add(v);
    };
    for (const cell of cells) {
      add(rowAxis, cell.row_parts?.[0]);
      add(rowAxis2, cell.row_parts?.[1]);
      add(colAxis, cell.col_key);
    }
    for (const lane of detail?.lanes ?? []) add("turn_owner", lane.lane);
    for (const lang of detail?.by_language ?? []) add("lang", lang.language);
    for (const fam of detail?.families ?? []) add(String(detail?.family_axis ?? ""), fam.family);
    for (const [k, v] of Object.entries(filters)) add(k, v);
    return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, [...v].sort()]));
  }, [cells, detail, rowAxis, rowAxis2, colAxis, filters]);

  const activeFilters = Object.entries(filters).filter(([, v]) => String(v ?? "").trim() !== "");

  const openCell = (rowKey: string, colKey: string) => {
    const cell = cellAt(rowKey, colKey);
    if (!cell || !cell.n) return;
    const next: Record<string, string> = { ...filters };
    if (family && detail?.family_axis) next[detail.family_axis] = family;
    if (rowAxis) next[rowAxis] = String(cell.row_parts?.[0] ?? "");
    if (rowAxis2) next[rowAxis2] = String(cell.row_parts?.[1] ?? "");
    if (colAxis) next[colAxis] = String(cell.col_key ?? "");
    setBox({
      open: true,
      filters: next,
      title: `${rowKey || "(blank)"} · ${axisLabel(colAxis)} ${colKey || "(blank)"}`,
      subtitle: `${fmtInt(cell.passed)} / ${fmtInt(cell.n)} turns passed · ${fmtMs(cell.p50_ms)} p50 · ${fmtInt(
        cell.tokens
      )} tok`,
      n: num(cell.n),
      passed: num(cell.passed),
    });
  };

  const primary = (cell: MatrixCell): string =>
    colourBy === "avoidable"
      ? cell.avoidable_share === null || cell.avoidable_share === undefined
        ? "—"
        : fmtPct(cell.avoidable_share, 0)
      : fmtPct(cell.pass_rate, 0);

  const secondary = (cell: MatrixCell): string => {
    if (colourBy !== "avoidable") return `n=${cell.n}`;
    if (cell.avoidable_share === null || cell.avoidable_share === undefined) return "unjudged";
    return `${fmtInt(cell.avoidable_tokens)} tok · n=${fmtInt(cell.necessity_n)}`;
  };

  const cellTitle = (row: string, col: string, cell: MatrixCell | null): string => {
    if (!cell) return "no turns in this cell";
    const waste =
      cell.avoidable_share === null || cell.avoidable_share === undefined
        ? "necessity: unjudged in this cell (no per-call record) — not 0% waste"
        : `avoidable ${fmtPct(cell.avoidable_share, 1)} = ${fmtInt(cell.avoidable_tokens)} tok over ${fmtInt(
            cell.necessity_n
          )} judged turns`;
    const zero =
      cell.pct_zero_llm === null || cell.pct_zero_llm === undefined
        ? "zero-LLM: unmetered"
        : `zero-LLM ${fmtPct(cell.pct_zero_llm, 0)}`;
    return `${row || "(blank)"} × ${col || "(blank)"}\n${cell.passed}/${cell.n} turns passed\np50 ${fmtMs(
      cell.p50_ms
    )} · ${fmtInt(cell.tokens)} tok\n${waste}\n${zero}`;
  };

  const necessityInstrumented = Boolean(detail?.necessity?.instrumented);

  return (
    <div className="stack">
      <Card
        title="Families in this run"
        note={
          <>
            grouped by <span className="strong">{axisLabel(detail?.family_axis)}</span>
            {detail?.family_axis_auto
              ? " — chosen automatically because it is the first axis this run's harness actually recorded"
              : ""}{" "}
            · click a card to filter everything below
          </>
        }
      >
        {!(detail?.families ?? []).length ? (
          <Empty title="No families" body="This run recorded no turns." />
        ) : (
          <div className="grid c3">
            {(detail?.families ?? []).map((fam) => (
              <button
                key={fam.family || "(blank)"}
                type="button"
                className="fam"
                aria-pressed={family === fam.family}
                onClick={() => onFamily(family === fam.family ? "" : fam.family)}
              >
                <div className="row">
                  <span className="truncate strong" style={{ fontSize: 11, textTransform: "uppercase" }}>
                    {fam.family || "(blank)"}
                  </span>
                  <span className="spacer" />
                  <span className="num strong">{fmtPct(fam.pass_rate)}</span>
                </div>
                <div className="muted" style={{ fontSize: 11 }}>
                  {fmtInt(fam.passed)}/{fmtInt(fam.n)} turns
                </div>
                <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
                  {Object.entries(fam.by_col ?? {}).map(([k, v]) => (
                    <span key={k} style={{ marginRight: 8 }}>
                      <span className="strong">{k || "—"}</span> {fmtPct(v.pass_rate, 0)} ({fmtInt(v.n)})
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card
        title={`Pass rate by segment${family ? ` · ${family}` : ""}`}
        note={
          <>
            Rows are scam strata × user strata, columns are language. Click a cell → all its cases open; click a case
            → the full pipeline. Cells with n &lt; {MIN_CELL_N} are drawn grey: a 0% cell over two turns is not a
            finding. Conversation-scoped failures attach to every turn of their case, so a cell reads as a
            turn-weighted <em>conversation</em> pass rate.
            {colourBy === "avoidable" && (
              <>
                {" "}
                <span className="strong">
                  Cells now show the share of judged tokens a deterministic judge says did not need a model. More is
                  worse; a cell with no judged turn reads “unjudged”, never 0%.
                </span>
                {!necessityInstrumented ? ` — ${detail?.necessity?.note ?? "nothing in this run was judged"}` : ""}
              </>
            )}
          </>
        }
      >
        <div className="toolbar">
          <label className="row" style={{ gap: 6 }}>
            <span className="muted" style={{ fontSize: 11 }}>
              Colour by
            </span>
            <select
              value={colourBy}
              onChange={(e) => setColourBy(e.target.value === "avoidable" ? "avoidable" : "pass")}
              style={{ width: 190 }}
              aria-label="Colour cells by"
            >
              <option value="pass">Pass rate</option>
              <option value="avoidable">Avoidable LLM share</option>
            </select>
          </label>
          <label className="row" style={{ gap: 6 }}>
            <span className="muted" style={{ fontSize: 11 }}>
              Row axis
            </span>
            <select value={rowAxis} onChange={(e) => onRowAxis(e.target.value)} style={{ width: 170 }} aria-label="Row axis">
              {AXES.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 6 }}>
            <span className="muted" style={{ fontSize: 11 }}>
              × second row axis
            </span>
            <select
              value={rowAxis2}
              onChange={(e) => onRowAxis2(e.target.value)}
              style={{ width: 170 }}
              aria-label="Second row axis"
            >
              <option value="">(none)</option>
              {AXES.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 6 }}>
            <span className="muted" style={{ fontSize: 11 }}>
              Columns
            </span>
            <select value={colAxis} onChange={(e) => onColAxis(e.target.value)} style={{ width: 170 }} aria-label="Column axis">
              {AXES.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {emptyAxes.length > 0 && (
          <Banner tone="warning">
            No turn in this run recorded {emptyAxes.join(" or ")} — that axis collapses to one blank key here. It is a
            missing field, not a homogeneous segment.
          </Banner>
        )}

        <div className="chips" style={{ marginBottom: "var(--sp-3)" }}>
          {AXES.map((axis) => (
            <select
              key={`f-${axis.key}`}
              value={filters[axis.key] ?? ""}
              onChange={(e) => onFilter(axis.key, e.target.value)}
              style={{ width: 168 }}
              aria-label={`Filter by ${axis.label}`}
            >
              <option value="">{axis.label}: all</option>
              {(axisValues[axis.key] ?? []).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          ))}
          {activeFilters.length > 0 && (
            <button className="btn sm ghost" onClick={onClearFilters}>
              ✕ Clear {activeFilters.length}
            </button>
          )}
        </div>

        {!cells.length ? (
          <Empty title="No cells" body="No turns for this run and filter set." />
        ) : (
          <div className="table-wrap">
            <table className="matrix">
              <thead>
                <tr>
                  <th className="rowlab">
                    {axisLabel(rowAxis)}
                    {rowAxis2 ? ` × ${axisLabel(rowAxis2)}` : ""} \ {axisLabel(colAxis)}
                  </th>
                  {colKeys.map((col) => (
                    <th key={col} title={col || "(blank)"}>
                      <span className="truncate" style={{ display: "block", maxWidth: "7rem" }}>
                        {col || "(blank)"}
                      </span>
                      <span className="sub">
                        {fmtPct(margins.cols.get(col)?.pass_rate ?? null, 0)} · n={fmtInt(margins.cols.get(col)?.n)}
                      </span>
                    </th>
                  ))}
                  <th>All</th>
                </tr>
              </thead>
              <tbody>
                {rowKeys.map((row) => (
                  <tr key={row}>
                    <th className="rowlab" title={row || "(blank)"}>
                      {row || "(blank)"}
                    </th>
                    {colKeys.map((col) => {
                      const cell = cellAt(row, col);
                      const bucket = colourBy === "avoidable" ? avoidableBucket(cell) : passBucket(cell);
                      return (
                        <td key={`${row}|${col}`}>
                          <button
                            type="button"
                            className={`cell ${bucket}${cell?.n ? " hit" : ""}`}
                            title={cellTitle(row, col, cell)}
                            onClick={() => openCell(row, col)}
                            disabled={!cell?.n}
                          >
                            {cell?.n ? (
                              <>
                                <span className="v">{primary(cell)}</span>
                                <span className="n">{secondary(cell)}</span>
                              </>
                            ) : (
                              <span className="n">·</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                    <td className="margin">
                      {fmtPct(margins.rows.get(row)?.pass_rate ?? null, 0)}{" "}
                      <span className="muted">n={fmtInt(margins.rows.get(row)?.n)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <CaseLightbox
        open={box.open}
        onClose={() => setBox((b) => ({ ...b, open: false }))}
        runId={runId}
        filters={box.filters}
        title={box.title}
        subtitle={box.subtitle}
        cellN={box.n}
        cellPassed={box.passed}
      />
    </div>
  );
}
